"use client";
import QuestionFilters from "@/app/components/filters/QuestionFilters";
import ResourceFilterAndBannerWrapper from "@/app/components/filters/ResourceFilterAndBannerWrapper";
import { QuestionDifficulty, QuestionFormat } from "@/enums/contentTypes";
import { getSubjectColorIndex } from "@/lib/strapi/formatters";
import { checkIfMultipleChoiceQuestion } from "@/lib/utils/helpers";
import { Box } from "@mantine/core";
import { Question, SubTopicQuestions } from "@tsTypes/question-schema";
import { Resources, SubTopic } from "@tsTypes/subject-schema";
import { useCheckUserAccess } from "@utils/accessChecks";
import { getUserPreferredSubTopicTitle } from "@utils/routing";
import { useSearchParams } from "next/navigation";
import { Fragment, useMemo, useState } from "react";
import {
  useMultipleSubTopicMeta,
  useSubTopicMeta,
} from "../../../../../../../components/CoreDataProvider";
import LinkedTopicResourceBanner from "../../../../components/banners/LinkedTopicResourceBanner";
import RestrictedResourceBanner from "../../../../components/banners/RestrictedResourceBanner";
import ContentSubHeader from "../../../../components/titles/ContentSubHeader";
import PreviewQuestionModule from "./PreviewQuestionModule";
import QuestionModule from "./QuestionModule";

export default function QuestionLayout({
  allQuestions,
  subjectName,
  subjectDocumentId,
  topicDocumentId,
  subTopicDocumentIds,
  subTopicDocumentId,
  linkedResource,
}: {
  allQuestions: SubTopicQuestions[] | Question[];
  subjectName: string;
  subjectDocumentId: string;
  topicDocumentId: string;
  subTopicDocumentIds: SubTopic["documentId"][];
  subTopicDocumentId?: string;
  linkedResource: Resources | null;
}) {
  const subTopicMeta = useSubTopicMeta(subTopicDocumentId ?? "");
  const subTopicMetaArray = useMultipleSubTopicMeta(subTopicDocumentIds);
  const subjectNameLower = subjectName.toLowerCase();
  const subjectColorIndex = getSubjectColorIndex(subjectNameLower);
  const userAccess = useCheckUserAccess("questions", subjectDocumentId);
  const [selectedDifficulty, setSelectedDifficulty] = useState<
    QuestionDifficulty[]
  >([]);
  const [selectedFormat, setSelectedFormat] = useState<QuestionFormat[]>([]);
  const currentSubTheme = useSearchParams().get("activeTheme")!;
  const questions = useMemo(() => {
    // if overall topic page render all questions split by subtopic
    if (!subTopicDocumentId) {
      return (allQuestions as SubTopicQuestions[]).map(
        ({ documentId: stDocumentId, questions: stQuestions }) => {
          const subTopicMeta = subTopicMetaArray[stDocumentId];
          return (
            <Fragment key={stDocumentId}>
              <ContentSubHeader
                color={subjectColorIndex}
                title={getUserPreferredSubTopicTitle(subTopicMeta)}
              />
              {stQuestions.length === 0 ? (
                <PreviewQuestionModule subjectColorIndex={subjectColorIndex} />
              ) : (
                stQuestions.map((question, indx) => {
                  return (
                    <QuestionModule
                      key={question.documentId}
                      question={question}
                      indx={indx}
                      subjectNameLower={subjectNameLower}
                      topicDocumentId={topicDocumentId}
                      subjectDocumentId={subjectDocumentId}
                      subTopicDocumentId={stDocumentId}
                      hiddenByFilters={checkIfHiddenByFilters(
                        selectedDifficulty,
                        selectedFormat,
                        question
                      )}
                      userAccess={userAccess}
                    />
                  );
                })
              )}
            </Fragment>
          );
        }
      );
    } else {
      return allQuestions.length === 0 ? (
        <PreviewQuestionModule subjectColorIndex={subjectColorIndex} />
      ) : (
        (allQuestions as Question[]).map((question, indx) => (
          <QuestionModule
            key={question.documentId}
            question={question as Question}
            indx={indx}
            subjectNameLower={subjectNameLower}
            topicDocumentId={topicDocumentId}
            subjectDocumentId={subjectDocumentId}
            subTopicDocumentId={subTopicDocumentId}
            currentSubTheme={currentSubTheme}
            hiddenByFilters={checkIfHiddenByFilters(
              selectedDifficulty,
              selectedFormat,
              question
            )}
            userAccess={userAccess}
          />
        ))
      );
    }
  }, [
    subjectNameLower,
    subjectColorIndex,
    allQuestions,
    subjectDocumentId,
    topicDocumentId,
    subTopicDocumentId,
    subTopicMetaArray,
    selectedDifficulty,
    selectedFormat,
    currentSubTheme,
    userAccess,
  ]);

  return (
    <>
      <ResourceFilterAndBannerWrapper hasFilters={true}>
        <RestrictedResourceBanner
          resource={"questions"}
          subjectDocumentId={subjectDocumentId}
          subjectName={subjectNameLower}
          userAccess={userAccess}
        />
        <QuestionFilters
          selectedDifficulty={selectedDifficulty}
          setSelectedDifficulty={setSelectedDifficulty}
          selectedFormat={selectedFormat}
          setSelectedFormat={setSelectedFormat}
        />
      </ResourceFilterAndBannerWrapper>
      <Box className="progressive-fade-in">{questions}</Box>
      <LinkedTopicResourceBanner
        subject={subjectNameLower}
        currentResource={"questions"}
        linkedResource={linkedResource}
        topicDocumentId={topicDocumentId}
        subTopicDocumentId={subTopicDocumentId}
        subTopicTitle={subTopicMeta?.title}
      />
    </>
  );
}

function checkIfHiddenByFilters(
  selectedDifficulty: QuestionDifficulty[],
  selectedFormat: QuestionFormat[],
  question: Question
) {
  if (
    (selectedDifficulty.length > 0 &&
      !selectedDifficulty.includes(question.Difficulty)) ||
    (selectedFormat.length > 0 &&
      !selectedFormat.includes(
        checkIfMultipleChoiceQuestion(question) ? "MC" : "LA"
      ))
  ) {
    return true;
  }
  return false;
}
